import { useCallback, useEffect, useRef, useState } from "react";
import {
  dispatchAppState,
  useRtfmAppState,
  type SceneReference,
} from "./useRtfmAppState.js";

const APP_READY_EVENT = "rtfm:app-ready";
const SCENE_LOAD_TIMEOUT = 7500;

declare global {
  interface Window {
    appStarted?: boolean;
    app?: {
      ws?: {
        on: (type: string, handler: (payload: unknown) => void) => unknown;
        off: (type: string, handler: (payload: unknown) => void) => unknown;
        sendCommand: (command: string, requestId: string) => void;
      };
      wsReady?: Promise<{
        on: (type: string, handler: (payload: unknown) => void) => unknown;
        off: (type: string, handler: (payload: unknown) => void) => unknown;
        sendCommand: (command: string, requestId: string) => void;
      }>;
      background?: {
        currentUrl: string;
      };
      scene?: unknown;
    };
    __rtfmSceneOverrides?: Record<string, SceneReference>;
  }
}

function generateRequestId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export interface SceneLoaderHook {
  loadScene: (worldId: string) => Promise<void>;
  resetSceneRequest: () => Promise<void>;
  randomSceneRequest: () => Promise<void>;
  toggleNumSteps: () => Promise<void>;
  numSteps: number;
}

export function useSceneLoader(): SceneLoaderHook {
  const { state } = useRtfmAppState();
  const stateRef = useRef(state);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const [app, setApp] = useState(() =>
    typeof window !== "undefined" ? window.app ?? null : null,
  );

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (window.app) {
      setApp(window.app);
    }

    const handleReady = (event: Event) => {
      const custom = event as CustomEvent<{ app?: typeof window.app }>;
      if (custom.detail?.app) {
        setApp(custom.detail.app);
      }
    };

    document.addEventListener(APP_READY_EVENT, handleReady as EventListener);
    return () => {
      document.removeEventListener(APP_READY_EVENT, handleReady as EventListener);
    };
  }, []);

  const requestIdRef = useRef<string | null>(null);
  const pendingWorldIdRef = useRef<string | null>(null);
  const sceneLoadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear pending timeout when state indicates success for the pending world
  useEffect(() => {
    const pending = pendingWorldIdRef?.current;
    if (!state.currentWorldId) {
      // console.warn("No current world ID, ok if first time loading");
      return;
    }

    if (state.currentWorldId !== pending) {
      // console.warn("No pending scene load, ok if first time loading");
    }

    if (sceneLoadTimerRef.current) {
      clearTimeout(sceneLoadTimerRef.current);
      sceneLoadTimerRef.current = null;
    }

    if(import.meta.env.MODE === "development") {
      console.log("new scene_id");
    }

    requestIdRef.current = null;
    pendingWorldIdRef.current = null;
  }, [state.currentWorldId]);

  //TODO: sync from server metadata on the steps?
  const [numSteps, setNumSteps] = useState(state.steps);
  const toggleNumSteps = useCallback(
    async () => {
      if (!app?.wsReady) {
        console.warn("WebSocket client unavailable");
        return;
      }

      try {
        const ws = await app.wsReady;
        if (!ws) {
          throw new Error("WebSocket client unavailable");
        }

        const newNumSteps = numSteps === 5 ? 1 : 5;
        setNumSteps(newNumSteps);
        // console.log(`quality: ${numSteps === 5 ? "high" : "low"} -> ${newNumSteps === 5 ? "high" : "low"}`);
        ws.sendCommand(`set_num_steps ${newNumSteps}`, generateRequestId());
      } catch (error) {
        console.error("Failed to reset scene", error);
      }
    }, [app, numSteps]);


  const resetSceneRequest = useCallback(
    async () => {
      if (!app?.wsReady) {
        console.warn("WebSocket client unavailable");
        return;
      }

      try {
        const ws = await app.wsReady;
        if (!ws) {
          throw new Error("WebSocket client unavailable");
        }

        ws.sendCommand("reset_camera", generateRequestId());
      } catch (error) {
        console.error("Failed to reset scene", error);
      }
    }, [app]);

  const loadScene = useCallback(
    async (worldId: string) => {

      if (stateRef.current.currentWorldId === worldId) {
        console.error("Scene already loaded.");
        return;
      }

      if (requestIdRef.current) {
        console.error("Scene load already in progress.");
        return;
      }

      const world = stateRef.current.worldsList[worldId];
      const target = world?.scene;
      if (!target) {
        console.error("Scene not configured for loading.");
        dispatchAppState({
          sceneLoadStatus: "idle",
          sceneLoadError: null,
          loadingWorldId: worldId,
        });
        return;
      }

      if (!app?.wsReady) {
        console.error("Renderer connection is not ready yet.");
        dispatchAppState({
          sceneLoadStatus: "idle",
          sceneLoadError: null,
          loadingWorldId: worldId,
        });
        return;
      }

      try {
        const ws = await app.wsReady;
        if (!ws) {
          throw new Error("WebSocket client unavailable");
        }

        const requestId = generateRequestId();
        requestIdRef.current = requestId;
        pendingWorldIdRef.current = worldId;

        dispatchAppState({
          sceneLoadStatus: "loading",
          sceneLoadError: null,
          loadingWorldId: worldId,
        });

        const command = `load_scene ${target.sceneType} ${target.sceneId}`;
        ws.sendCommand(command, requestId);

        // Start a 5s timeout waiting for app.js onMetadata to complete the load
        if (sceneLoadTimerRef.current) {
          clearTimeout(sceneLoadTimerRef.current);
          sceneLoadTimerRef.current = null;
        }
        sceneLoadTimerRef.current = setTimeout(() => {
          if (!requestIdRef.current) {
            return; // already handled
          }
          const pending = pendingWorldIdRef.current || worldId;
          requestIdRef.current = null;
          pendingWorldIdRef.current = null;
          console.error("Scene load timed out waiting for metadata.");
          dispatchAppState({
            sceneLoadStatus: "idle",
            sceneLoadError: "scene load timed out",
            loadingWorldId: pending,
          });
        }, SCENE_LOAD_TIMEOUT);
      } catch (error) {
        console.error("Failed to initiate scene load command", error);
        requestIdRef.current = null;
        pendingWorldIdRef.current = null;
        if (sceneLoadTimerRef.current) {
          clearTimeout(sceneLoadTimerRef.current);
          sceneLoadTimerRef.current = null;
        }
        dispatchAppState({
          sceneLoadStatus: "idle",
          sceneLoadError: "Failed to initiate scene load.",
          loadingWorldId: "",
        });
      }
    },
    [app],
  );

  const randomSceneRequest = useCallback(async () => {
    const worldsWithoutCurrent = Object.keys(stateRef.current.worldsList).filter(worldId => worldId !== stateRef.current.currentWorldId);
    await loadScene(worldsWithoutCurrent[Math.floor(Math.random() * worldsWithoutCurrent.length)]);
  }, [loadScene]);

  return {
    loadScene,
    resetSceneRequest,
    randomSceneRequest,
    toggleNumSteps,
    numSteps,
  };
}

